// shell/components/select-control.ts
// A styled <select> for a pane's title row (or any toolbar-grade row).
//
// The title row cannot hold a form field, but it can hold a *scope*: the
// version a schema detail is showing, a camera's frame rate, an agent's
// model. Those are selects, not buttons, and they apply on change — there is
// no separate commit. `renderPaneActions` builds one for every
// `{ kind: 'select' }` descriptor; panes that hand-roll a row can call this
// directly.

import { createElement } from '../utils/dom';

export interface SelectControlOption {
    value: string;
    label: string;
    /**
     * Tooltip for this option alone — what choosing it means, when the label
     * is too short to say ("Ask before every write"). Omitted options carry no
     * `title` attribute at all.
     */
    title?: string;
}

export interface SelectControlConfig {
    /** Currently selected value. Ignored when it matches no option. */
    value?: string;
    options: readonly SelectControlOption[];
    disabled?: boolean;
    /** Tooltip. When the control is disabled, this should say why. */
    title?: string;
    /** `'small'` for a title row; `'medium'` (the default) elsewhere. */
    size?: 'small' | 'medium';
    /** Additional CSS class. */
    className?: string;
    /** Fires on every selection change, with the chosen option's value. */
    onChange: (value: string) => void;
}

/**
 * Build a shell-styled select.
 *
 * @example
 * ```typescript
 * const version = createSelectControl({
 *     value: current.serial,
 *     options: serials.map(s => ({ value: s.id, label: `v${s.number}` })),
 *     size: 'small',
 *     title: 'Schema version',
 *     onChange: (id) => showSerial(id),
 * });
 * titleRow.appendChild(version);
 * ```
 */
export function createSelectControl(config: SelectControlConfig): HTMLSelectElement {
    const { value, options, disabled = false, title, size = 'medium', className, onChange } = config;

    const classes = ['cs-select'];
    if (size === 'small') classes.push('cs-select--small');
    if (className) classes.push(className);

    const select = createElement('select', {
        className: classes.join(' '),
        attributes: title ? { title } : {},
    });

    for (const option of options) {
        const opt = createElement('option', {
            textContent: option.label,
            attributes: option.title !== undefined
                ? { value: option.value, title: option.title }
                : { value: option.value },
        });
        select.appendChild(opt);
    }

    // Assigning an unknown value would blank the select; leave the browser's
    // default (the first option) selected instead.
    if (value !== undefined && options.some(o => o.value === value)) {
        select.value = value;
    }

    if (disabled) select.disabled = true;

    select.addEventListener('change', () => onChange(select.value));

    return select;
}
